import {Injectable} from '@angular/core';
import {PerformanceMetrics} from '../models/metrics';


interface ConfusionMatrix {
  tp: number;
  tn: number;
  fp: number;
  fn: number;
}

@Injectable({
  providedIn: 'root'
})
export class MetricsService {

  /**
   * Calculates performance metrics, adjusted for the number of unanalyzed records.
   *
   * @param yTrue - Array of true labels (0 or 1)
   * @param yPred - Array of predicted labels (0 or 1)
   * @param totalRecords - Total number of records in the dataset
   * @param analyzedRecords - Number of records that were successfully analyzed
   * @returns An object containing the calculated metrics
   */
  calculatePerformanceMetrics(
    yTrue: number[],
    yPred: number[],
    totalRecords: number,
    analyzedRecords: number
  ): PerformanceMetrics {
    const confusion = this.getConfusionMatrix(yTrue, yPred);

    const accuracy = this.accuracyScore(confusion)
    const precision = this.precisionScore(confusion, 0)
    const recall = this.recallScore(confusion, 0)
    const f1 = this.f1Score(confusion, 0)
    const mcc = this.matthewsCorrcoef(confusion)
    const kappa = this.cohenKappaScore(confusion)
    const pabak = this.calculatePabak(confusion)

    // Calculate the percentage of data that was analyzed
    const analysisCoverage = totalRecords > 0 ? analyzedRecords / totalRecords : 0;

    // Calculate adjustment factor
    let adjustmentFactor = 1.0; // Default to no adjustment needed
    if (analyzedRecords < totalRecords) {
      adjustmentFactor = analyzedRecords / totalRecords;
    }

    // If the adjustment factor is very small, metrics might not be meaningful
    if (adjustmentFactor < 0.5) { // You can adjust this threshold as needed
      console.warn(`Warning: Only ${(adjustmentFactor * 100).toFixed(2)}% of records were analyzed. Metrics might be unreliable.`);
    }

    // Apply adjustment factor only if needed
    const adjustedAccuracy = accuracy * adjustmentFactor;
    const adjustedPrecision = precision * adjustmentFactor;
    const adjustedRecall = recall * adjustmentFactor;
    const adjustedF1 = f1 * adjustmentFactor;
    const adjustedMcc = mcc * adjustmentFactor;
    const adjustedKappa = kappa * adjustmentFactor;
    const adjustedPabak = pabak * adjustmentFactor;

    return {
      accuracy: adjustedAccuracy,
      precision: adjustedPrecision,
      recall: adjustedRecall,
      f1: adjustedF1,
      mcc: adjustedMcc,
      kappa: adjustedKappa,
      pabak: adjustedPabak,
      analysisCoverage
    } as PerformanceMetrics;
  }

  private getConfusionMatrix(yTrue: number[], yPred: number[]): { tp: number; tn: number; fp: number; fn: number } {
    let tp = 0, tn = 0, fp = 0, fn = 0;
    for (let i = 0; i < yTrue.length; i++) {
      if (yTrue[i] === 1 && yPred[i] === 1) {
        tp++;
      } else if (yTrue[i] === 0 && yPred[i] === 0) {
        tn++;
      } else if (yTrue[i] === 0 && yPred[i] === 1) {
        fp++;
      } else if (yTrue[i] === 1 && yPred[i] === 0) {
        fn++;
      }
    }
    return {tp, tn, fp, fn} as ConfusionMatrix;
  }


  private accuracyScore(confusion: ConfusionMatrix): number {
    const total = confusion.tp + confusion.tn + confusion.fp + confusion.fn;
    return total > 0 ? (confusion.tp + confusion.tn) / total : 0;
  }


  private precisionScore(confusion: ConfusionMatrix, zeroDivision: number = 0): number {
    return (confusion.tp + confusion.fp) > 0 ? confusion.tp / (confusion.tp + confusion.fp) : zeroDivision;
  }


  private recallScore(confusion: ConfusionMatrix, zeroDivision: number = 0): number {
    return (confusion.tp + confusion.fn) > 0 ? confusion.tp / (confusion.tp + confusion.fn) : zeroDivision;
  }


  private f1Score(confusion: ConfusionMatrix, zeroDivision: number = 0): number {
    const prec = this.precisionScore(confusion, zeroDivision);
    const rec = this.recallScore(confusion, zeroDivision);
    return (prec + rec) > 0 ? (2 * prec * rec) / (prec + rec) : zeroDivision;
  }

  private matthewsCorrcoef(confusion: ConfusionMatrix): number {
    const numerator = (confusion.tp * confusion.tn) - (confusion.fp * confusion.fn);
    const denominator = Math.sqrt((confusion.tp + confusion.fp) * (confusion.tp + confusion.fn) * (confusion.tn + confusion.fp) * (confusion.tn + confusion.fn));
    return denominator !== 0 ? numerator / denominator : 0;
  }


  private cohenKappaScore(confusion: ConfusionMatrix): number {
    const total = confusion.tp + confusion.tn + confusion.fp + confusion.fn;
    if (total === 0) return 0;

    const observedAccuracy = (confusion.tp + confusion.tn) / total;
    const actualPos = confusion.tp + confusion.fn;
    const actualNeg = confusion.tn + confusion.fp;
    const predictedPos = confusion.tp + confusion.fp;
    const predictedNeg = confusion.tn + confusion.fn;
    const expectedAccuracy = ((actualPos * predictedPos) + (actualNeg * predictedNeg)) / (total * total);
    return (1 - expectedAccuracy) !== 0 ? (observedAccuracy - expectedAccuracy) / (1 - expectedAccuracy) : 0;
  }

  private calculatePabak(confusion: ConfusionMatrix): number {
    const recallPos = (confusion.tp + confusion.fn) > 0 ? confusion.tp / (confusion.tp + confusion.fn) : 0;
    const recallNeg = (confusion.tn + confusion.fp) > 0 ? confusion.tn / (confusion.tn + confusion.fp) : 0;
    return (recallPos + recallNeg) / 2;
  }
}
